import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

type Status = WintSuperkollAPIModel.VatReportStatus;
const Status = WintSuperkollAPIModel.VatReportStatus;
type ActionReason = WintSuperkollAPIModel.VatReportActionReason;
const ActionReason = WintSuperkollAPIModel.VatReportActionReason;

/** The API types dates as `Date`, but they reach the browser as ISO strings. */
export type DateLike = Date | string;

export type VatReportStatusLabel =
  | "Draft"
  | "Booked"
  | "BookedFiledByCustomer"
  | "AwaitingSignature"
  | "Submitted"
  | "ActionRequired"
  | "BeingRemoved"
  | "Unknown";

export type VatReportTagTone = "blue" | "green" | "red" | "gray";

export interface VatReportStatusAppearance {
  label: VatReportStatusLabel;
  tone: VatReportTagTone;
}

// Colours follow the viewer-relative rule in misc-elements.md: blue is "someone else acts" (the
// customer signs), and the advisor has nothing to act on in a read-only view, so yellow never
// appears. Draft, Booked and BeingRemoved are states nobody is waiting on from this screen.
export const getVatReportStatusAppearance = (status: Status, filedByCustomer: boolean): VatReportStatusAppearance => {
  switch (status) {
    case Status.Draft:
      return { label: "Draft", tone: "gray" };
    case Status.Booked:
      return { label: filedByCustomer ? "BookedFiledByCustomer" : "Booked", tone: "gray" };
    case Status.AwaitingSignature:
      return { label: "AwaitingSignature", tone: "blue" };
    case Status.Submitted:
      return { label: "Submitted", tone: "green" };
    case Status.ActionRequired:
      return { label: "ActionRequired", tone: "red" };
    case Status.BeingRemoved:
      return { label: "BeingRemoved", tone: "gray" };
    default:
      return { label: "Unknown", tone: "gray" };
  }
};

export type VatReportActionReasonKey =
  | "UploadFailed"
  | "Rejected"
  | "AuthorizationFailure"
  | "MissingPeriod"
  | "StoppedByValidation";

export const getVatReportActionReasonKey = (
  status: Status,
  reason: ActionReason
): VatReportActionReasonKey | undefined => {
  if (status !== Status.ActionRequired) return undefined;

  switch (reason) {
    case ActionReason.UploadFailed:
      return "UploadFailed";
    case ActionReason.Rejected:
      return "Rejected";
    case ActionReason.AuthorizationFailure:
      return "AuthorizationFailure";
    case ActionReason.MissingPeriod:
      return "MissingPeriod";
    case ActionReason.StoppedByValidation:
      return "StoppedByValidation";
    default:
      return undefined;
  }
};

export enum VatReportLifecycleStep {
  Saved = 0,
  Booked = 1,
  AwaitingSignature = 2,
  Submitted = 3,
}

const LIFECYCLE: VatReportLifecycleStep[] = [
  VatReportLifecycleStep.Saved,
  VatReportLifecycleStep.Booked,
  VatReportLifecycleStep.AwaitingSignature,
  VatReportLifecycleStep.Submitted,
];

// ActionRequired and BeingRemoved both concern a declaration Wint had already created from a
// booked report, but the API does not say how far it got before it failed or was pulled back.
// Booked is the furthest step that is certain, so the progress stops there and the tag says the rest.
export const getReachedVatReportLifecycleStep = (status: Status): VatReportLifecycleStep => {
  switch (status) {
    case Status.Booked:
    case Status.ActionRequired:
    case Status.BeingRemoved:
      return VatReportLifecycleStep.Booked;
    case Status.AwaitingSignature:
      return VatReportLifecycleStep.AwaitingSignature;
    case Status.Submitted:
      return VatReportLifecycleStep.Submitted;
    default:
      return VatReportLifecycleStep.Saved;
  }
};

// Wint never sends a booked declaration the customer files, so its later steps would sit there looking stuck;
// the status tag says "lämnas av kunden" instead. A draft still shows where it stands.
export const showsVatReportLifecycle = (status: Status, filedByCustomer: boolean): boolean =>
  !(status === Status.Booked && filedByCustomer);

export const getVatReportLifecycle = (status: Status): { step: VatReportLifecycleStep; active: boolean }[] => {
  const reached = getReachedVatReportLifecycleStep(status);
  return LIFECYCLE.map((step) => ({ step, active: step === reached }));
};

interface DateParts {
  year: number;
  month: number;
  day: number;
}

// Read off the ISO string rather than through `new Date`, which would shift a midnight date into the
// previous day for any viewer west of the server.
const toDateParts = (value: DateLike): DateParts => {
  if (typeof value === "string") {
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);
    return { year, month, day };
  }
  return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
};

const pad = (n: number) => String(n).padStart(2, "0");

export const formatVatDate = (value: DateLike): string => {
  const { year, month, day } = toDateParts(value);
  return `${year}-${pad(month)}-${pad(day)}`;
};

const lastDayOfMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * "augusti 2026" for a monthly period, "juli – september 2026" for a quarter, and a plain date range
 * for anything that does not start and end on month boundaries.
 */
export const formatVatPeriod = (start: DateLike, end: DateLike, locale: string): string => {
  const s = toDateParts(start);
  const e = toDateParts(end);

  if (s.day !== 1 || e.day !== lastDayOfMonth(e.year, e.month)) {
    return `${formatVatDate(start)} – ${formatVatDate(end)}`;
  }

  const monthYear = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  const monthOnly = new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" });
  const startDate = Date.UTC(s.year, s.month - 1, 1);
  const endDate = Date.UTC(e.year, e.month - 1, 1);

  if (s.year === e.year && s.month === e.month) return monthYear.format(startDate);
  if (s.year === e.year) return `${monthOnly.format(startDate)} – ${monthYear.format(endDate)}`;
  return `${monthYear.format(startDate)} – ${monthYear.format(endDate)}`;
};

export type VatReportSectionKey = "A" | "B" | "C" | "D" | "H" | "I" | "E" | "F" | "G";

// Skatteverket's form order, which is not alphabetical: H and I (import) sit between D and E.
const SECTIONS: { key: VatReportSectionKey; codes: string[] }[] = [
  { key: "A", codes: ["05", "06", "07", "08"] },
  { key: "B", codes: ["10", "11", "12"] },
  { key: "C", codes: ["20", "21", "22", "23", "24"] },
  { key: "D", codes: ["30", "31", "32"] },
  { key: "H", codes: ["50"] },
  { key: "I", codes: ["60", "61", "62"] },
  { key: "E", codes: ["35", "36", "37", "38", "39", "40", "41", "42"] },
  { key: "F", codes: ["48"] },
  { key: "G", codes: ["49"] },
];

export interface VatReportBoxSection<T> {
  /** Undefined for boxes the form sections do not know, which are kept rather than dropped. */
  key?: VatReportSectionKey;
  boxes: T[];
}

export const groupVatReportBoxes = <T extends { code?: string }>(boxes: T[] = []): VatReportBoxSection<T>[] => {
  const known = new Set(SECTIONS.flatMap((s) => s.codes));
  const sections: VatReportBoxSection<T>[] = SECTIONS.map((section) => ({
    key: section.key,
    boxes: boxes.filter((b) => b.code !== undefined && section.codes.includes(b.code)),
  })).filter((section) => section.boxes.length > 0);

  const unknown = boxes.filter((b) => b.code === undefined || !known.has(b.code));
  return unknown.length ? [...sections, { boxes: unknown }] : sections;
};

export const VAT_REPORT_TOTAL_BOX = "49";

export const isVatReportTotalBox = (code?: string) => code === VAT_REPORT_TOTAL_BOX;

export type VatAmountDirection = "ToPay" | "ToReceive";

/** Box 49 is positive when the company pays and negative when it gets money back. */
export const getVatAmountDirection = (amount?: number | null): VatAmountDirection | undefined => {
  if (amount === undefined || amount === null || amount === 0) return undefined;
  return amount > 0 ? "ToPay" : "ToReceive";
};
